import React, { useState } from 'react';
import { Download, FileText, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import useScrollReveal from '../hooks/useScrollReveal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhitepaperCTA = () => {
  const [ref, revealed] = useScrollReveal(0.15);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    accept_privacy: false,
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!form.name.trim() || !form.email.trim()) {
      setErrorMsg('Bitte Name und E-Mail angeben.');
      return;
    }
    if (!form.accept_privacy) {
      setErrorMsg('Bitte stimmen Sie der Datenschutzerklärung zu.');
      return;
    }

    setStatus('loading');
    try {
      const res = await axios.post(`${API}/whitepaper/request`, form);
      const url = res.data.download_url;
      setDownloadUrl(url);
      setStatus('success');

      // Auto-trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename || 'whitepaper.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      setStatus('error');
      setErrorMsg(
        err?.response?.data?.detail ||
          'Es gab einen Fehler. Bitte nutzen Sie unser Kontaktformular am Seitenende.'
      );
    }
  };

  const hebel = [
    'Briefkonsolidierung & Konsolidierer-Auswahl',
    'Portoklassen-Optimierung',
    'Dienstleister- & Vertragssteuerung',
    'Adress- & Datenqualität',
    'Prozesse & Poststellen-Automatisierung',
  ];

  return (
    <section
      id="whitepaper"
      className="py-20 lg:py-32 bg-slate-900 relative overflow-hidden"
      data-testid="whitepaper-section"
    >
      {/* Decorative accent shapes */}
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10 blur-3xl"
        style={{ backgroundColor: '#FFA266' }}
      />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-slate-700 opacity-20 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div
          ref={ref}
          className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center reveal-on-scroll ${
            revealed ? 'revealed' : ''
          }`}
        >
          {/* LEFT: Content */}
          <div className="text-white">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-6"
              style={{ backgroundColor: 'rgba(255, 162, 102, 0.15)', color: '#FFA266' }}
            >
              <FileText size={14} />
              Material zum Mitnehmen
            </div>

            <h2 className="text-3xl lg:text-5xl font-bold mb-6 leading-tight">
              5 Hebel zur{' '}
              <span style={{ color: '#FFA266' }}>Portooptimierung</span>
            </h2>

            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              Die Methodik im Überblick — kompakt aufbereitet aus 15+ Jahren
              Branchenerfahrung. Für Kliniken, Labore und MVZ, kommunale und soziale Einrichtungen – als Diskussionsgrundlage für Leitung, Einkauf und Poststelle.
            </p>

            <ul className="space-y-3 mb-8">
              {hebel.map((h, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-slate-200"
                  data-testid={`whitepaper-hebel-${i + 1}`}
                >
                  <CheckCircle2
                    size={20}
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: '#FFA266' }}
                  />
                  <span>
                    <span className="font-semibold text-white">Hebel {i + 1}: </span>
                    {h}
                  </span>
                </li>
              ))}
            </ul>

            <p className="text-sm text-slate-400">
              7 Seiten · DIN A4 · zur internen Diskussion und Weitergabe
            </p>
          </div>

          {/* RIGHT: Form Card */}
          <div className="relative">
            <div
              className="absolute -inset-1 rounded-2xl opacity-30 blur-lg"
              style={{ backgroundColor: '#FFA266' }}
            />
            <div className="relative bg-white rounded-2xl shadow-2xl p-8 lg:p-10">
              {status === 'success' ? (
                <div className="text-center py-6" data-testid="whitepaper-success">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: 'rgba(255, 162, 102, 0.15)' }}
                  >
                    <Download size={28} style={{ color: '#F08C4A' }} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    Vielen Dank!
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Ihr Download wurde gestartet. Falls nicht, klicken Sie bitte hier:
                  </p>
                  <a
                    href={downloadUrl}
                    download="5-Hebel-zur-Portooptimierung.pdf"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-slate-900 hover:scale-105 transition-transform shadow-lg"
                    style={{ backgroundColor: '#FFA266' }}
                    data-testid="whitepaper-manual-download"
                  >
                    <Download size={18} />
                    Whitepaper jetzt laden
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="whitepaper-form">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      PDF anfordern
                    </h3>
                    <p className="text-sm text-slate-500 mb-2">
                      Tragen Sie sich kurz ein — das PDF startet sofort als Download.
                    </p>
                  </div>

                  <Input
                    type="text"
                    placeholder="Vor- und Nachname *"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    disabled={status === 'loading'}
                    data-testid="whitepaper-name-input"
                  />
                  <Input
                    type="email"
                    placeholder="Geschäftliche E-Mail *"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    disabled={status === 'loading'}
                    data-testid="whitepaper-email-input"
                  />
                  <Input
                    type="text"
                    placeholder="Unternehmen (optional)"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    disabled={status === 'loading'}
                    data-testid="whitepaper-company-input"
                  />

                  <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer leading-relaxed pt-1">
                    <input
                      type="checkbox"
                      checked={form.accept_privacy}
                      onChange={(e) =>
                        setForm({ ...form, accept_privacy: e.target.checked })
                      }
                      className="mt-0.5 cursor-pointer flex-shrink-0"
                      required
                      data-testid="whitepaper-privacy-checkbox"
                    />
                    <span>
                      Ich habe die{' '}
                      <a
                        href="/datenschutz"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-800 underline hover:text-slate-600 font-medium"
                      >
                        Datenschutzerklärung
                      </a>{' '}
                      gelesen und stimme der Verarbeitung meiner Daten zur einmaligen Zusendung des Whitepapers zu. Es erfolgt keine Aufnahme in einen Newsletter. *
                    </span>
                  </label>

                  {errorMsg && (
                    <p className="text-sm text-red-600" data-testid="whitepaper-error">
                      {errorMsg}
                    </p>
                  )}

                  <Button
                    type="submit"
                    disabled={status === 'loading' || !form.accept_privacy}
                    className="w-full text-base font-semibold py-6 text-slate-900 hover:scale-[1.02] transition-transform shadow-lg disabled:opacity-60 disabled:hover:scale-100"
                    style={{ backgroundColor: '#FFA266' }}
                    data-testid="whitepaper-submit-btn"
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 size={18} className="mr-2 animate-spin" />
                        Wird erstellt ...
                      </>
                    ) : (
                      <>
                        <Download size={18} className="mr-2" />
                        PDF anfordern
                        <ArrowRight size={18} className="ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-slate-400 pt-1">
                    Ihre Daten werden vertraulich behandelt - kein Spam, kein Newsletter.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhitepaperCTA;
